import { RoleKey, RoleNameMap } from '../account/hooks/useRoles';

// Define role hierarchy (higher index = higher privilege)
export const roleHierarchy = [
  RoleNameMap[RoleKey.RESTRICTED],
  RoleNameMap[RoleKey.GENERAL],
  RoleNameMap[RoleKey.WORKSPACE_ADMIN],
  RoleNameMap[RoleKey.GLOBAL_ADMIN],
];

/**
 * Check if a user's role hierarchy allows them to modify/assign a target role
 * @param currentUserRole - The role of the current user
 * @param targetRole - The role being modified or assigned
 * @returns true if hierarchy allows the operation
 */
export const canModifyUserRole = (
  currentUserRole: string,
  targetRole: string
): boolean => {
  const currentUserIndex = roleHierarchy.indexOf(currentUserRole);
  const targetRoleIndex = roleHierarchy.indexOf(targetRole);

  // Can only modify/assign roles at same level or below in hierarchy
  return currentUserIndex >= targetRoleIndex;
};

/**
 * Get available roles for a user based on their role hierarchy and Global Admin visibility
 * NOTE: This only filters by hierarchy. The account.changeRole permission must be checked separately.
 * @param roleList - List of all available roles
 * @param currentUserRole - The role of the current user
 * @param showGlobalAdminRole - Whether to show Global Admin role
 * @returns Filtered list of roles based on hierarchy
 */
export const getAvailableRoles = (
  roleList: Array<{ id: string; name: string }>,
  currentUserRole?: string,
  showGlobalAdminRole: boolean = false
): Array<{ id: string; name: string }> => {
  let filteredRoles = roleList;

  // Filter based on current user's role hierarchy
  if (currentUserRole) {
    filteredRoles = filteredRoles.filter((role) =>
      canModifyUserRole(currentUserRole, role.name)
    );
  }

  // Filter out Global Admin role if not allowed
  if (!showGlobalAdminRole) {
    filteredRoles = filteredRoles.filter(
      (role) => role.name !== 'Global Admin'
    );
  }

  return filteredRoles;
};
